import { SyntheticEvent } from 'react';
import { X } from 'lucide-react';
import { PLUS_SIGN } from '@sib/config';
import { cn } from '../../utils';
import { ComboboxOption } from './Combobox';
import { comboboxMultiSelectPillClassName } from './ComboboxMultiSelectPillLabels';

export type MultiSelectValueVariant = 'default' | 'pills';

interface ComboboxMultiSelectValueProps<
  T extends Record<string, unknown> = {},
> {
  options: ComboboxOption<T>[];
  variant?: MultiSelectValueVariant;
  visibleOptionsCount?: number;
  onRemoveItem?: (value: string, event: SyntheticEvent) => void;
  readOnly?: boolean;
}

export const ComboboxMultiSelectValue = <
  T extends Record<string, unknown> = {},
>({
  options,
  variant = 'default',
  visibleOptionsCount = 2,
  onRemoveItem,
  readOnly,
}: ComboboxMultiSelectValueProps<T>) => {
  const isPillsVariant = variant === 'pills';
  const visibleOptions = isPillsVariant
    ? options
    : options.slice(0, visibleOptionsCount);
  const count = isPillsVariant
    ? 0
    : Math.max(options.length - visibleOptionsCount, 0);

  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 gap-1',
        isPillsVariant ? 'flex-wrap' : 'overflow-hidden'
      )}
    >
      {visibleOptions.map((option, index) => (
        <div
          key={`${option.value}-${index}`}
          className={cn(
            isPillsVariant
              ? comboboxMultiSelectPillClassName
              : 'flex min-w-0 items-center gap-1 rounded-md px-2 py-0.5 text-sm'
          )}
        >
          <span className="min-w-0 truncate">{option.label}</span>
          {!isPillsVariant && !readOnly && onRemoveItem && (
            <span
              role="button"
              tabIndex={0}
              className="hover:bg-secondary-foreground/20 ml-1 shrink-0 rounded-full p-0.5"
              onClick={e => onRemoveItem(option.value, e)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRemoveItem(option.value, e);
                }
              }}
            >
              <X className="size-3" />
            </span>
          )}
        </div>
      ))}
      {count > 0 && (
        <div className="flex items-center rounded-md px-2 py-0.5 text-sm">
          {`${PLUS_SIGN}${count}`}
        </div>
      )}
    </div>
  );
};
